import { z } from 'zod';

export const productSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  name: z.string()
    .min(1, 'El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(200, 'El nombre no puede exceder 200 caracteres')
    .trim(),
  sku: z.string()
    .min(1, 'El SKU es requerido')
    .max(50, 'El SKU no puede exceder 50 caracteres')
    .trim()
    .regex(/^[A-Za-z0-9\-_]+$/, 'El SKU solo puede contener letras, números, guiones y guiones bajos'),
  price: z.union([z.string(), z.number()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return null;
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    })
    .refine((val) => {
      if (val === null) return true;
      return val >= 0;
    }, 'El precio debe ser mayor o igual a 0'),
  description: z.string()
    .max(1000, 'La descripción no puede exceder 1000 caracteres')
    .optional(),
  items: z.array(z.object({
    id: z.union([z.string(), z.number()]),
    quantity: z.union([z.string(), z.number()]).transform(val => Number(val)).refine(val => val > 0, 'Cantidad debe ser mayor a 0'),
    unit: z.string().min(1, 'Unidad requerida')
  })).optional().default([])
});

export const productFormSchema = productSchema.omit({ id: true });

// Esquemas para diferentes operaciones
export const createProductSchema = productFormSchema;
export const updateProductSchema = productFormSchema.partial().extend({
  name: z.string().min(1, 'El nombre es requerido'),
  sku: z.string().min(1, 'El SKU es requerido')
});

// Esquema para filtros de búsqueda
export const productFiltersSchema = z.object({
  name: z.string().optional(),
  sku: z.string().optional(),
  active: z.union([z.boolean(), z.string()]).optional(),
  category_id: z.union([z.string(), z.number()]).optional(),
  brand: z.string().optional(),
  min_price: z.union([z.string(), z.number()]).optional(),
  max_price: z.union([z.string(), z.number()]).optional()
});

// Constantes para el frontend
export const PRODUCT_STATUSES = [
  { value: true, label: 'Activo' },
  { value: false, label: 'Inactivo' }
];

export const PRODUCT_UNITS = [
  { value: 'unit', label: 'Unidad' },
  { value: 'kg', label: 'Kilogramo' },
  { value: 'g', label: 'Gramo' },
  { value: 'lb', label: 'Libra' },
  { value: 'oz', label: 'Onza' },
  { value: 'l', label: 'Litro' },
  { value: 'ml', label: 'Mililitro' },
  { value: 'm', label: 'Metro' },
  { value: 'cm', label: 'Centímetro' },
  { value: 'mm', label: 'Milímetro' },
  { value: 'ft', label: 'Pie' },
  { value: 'in', label: 'Pulgada' },
  { value: 'box', label: 'Caja' },
  { value: 'pack', label: 'Paquete' },
  { value: 'dozen', label: 'Docena' },
  { value: 'pair', label: 'Par' }
];

export const WARRANTY_PERIODS = [
  { value: '0', label: 'Sin garantía' },
  { value: '30d', label: '30 días' },
  { value: '90d', label: '90 días' },
  { value: '6m', label: '6 meses' },
  { value: '1y', label: '1 año' },
  { value: '2y', label: '2 años' },
  { value: '3y', label: '3 años' },
  { value: '5y', label: '5 años' },
  { value: 'lifetime', label: 'De por vida' }
];

// Validación de un producto individual
export const validateProduct = (data) => {
  try {
    return {
      success: true,
      data: productFormSchema.parse(data),
      errors: {}
    };
  } catch (error) {
    const errors = {};
    error.errors.forEach((err) => {
      errors[err.path[0]] = err.message;
    });
    return {
      success: false,
      data: null,
      errors
    };
  }
};

// Función helper para limpiar datos del producto
export const sanitizeProductData = (data) => {
  // Remover campos vacíos o undefined
  const allowed = ['name', 'sku', 'price', 'description', 'items'];
  const cleaned = Object.fromEntries(
    Object.entries(data).filter(([key, value]) => {
      if (!allowed.includes(key)) return false;
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0 && key !== 'items') return false;
      return true;
    })
  );
  // Ensure items is an array of {id, quantity, unit}
  if ('items' in cleaned && Array.isArray(cleaned.items)) {
    cleaned.items = cleaned.items.map(item => {
      if (typeof item === 'object' && item !== null && 'id' in item && 'quantity' in item && 'unit' in item) {
        return { ...item, quantity: Number(item.quantity) };
      }
      // fallback for legacy: just id
      return { id: item, quantity: 1, unit: 'unit' };
    });
  }
  return cleaned;
};

// Función para formatear precio para mostrar
export const formatPrice = (price, currency = '$') => {
  if (price === null || price === undefined || isNaN(price)) {
    return '—';
  }
  return `${currency}${parseFloat(price).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Función para formatear stock
export const formatStock = (quantity, unit = 'unit') => {
  if (quantity === null || quantity === undefined || isNaN(quantity)) {
    return '—';
  }
  const unitLabel = PRODUCT_UNITS.find(u => u.value === unit)?.label || unit;
  return `${quantity} ${unitLabel}${quantity !== 1 && unitLabel === 'Unidad' ? 'es' : ''}`;
}; 